import { useState, useMemo } from "react";
import { X, ClipboardPaste, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { quickImportCustomers, quickImportToGroup } from "../services/customerServices";

const MIN = 2;
const MAX = 500;

function parseRows(text) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, idx) => {
      // Extract phone: optional leading +, then 7+ digits (allows spaces/dashes within)
      const phoneMatch = line.match(/\+?\d[\d\s\-]{5,}\d|\+?\d{7,}/);
      let name = "";
      let phoneNumber = "";
      if (phoneMatch) {
        // Strip spaces and dashes but keep digits (and drop leading +, backend normalises anyway)
        phoneNumber = phoneMatch[0].replace(/[\s\-+]/g, "");
        // Remove the matched phone block then strip surrounding separators including stray +
        name = line
          .replace(phoneMatch[0], "")
          .replace(/^[\s,;\t:|+]+|[\s,;\t:|+]+$/g, "")
          .trim();
      }
      // Phone is the only required field — name is optional
      const valid = Boolean(phoneNumber && phoneNumber.replace(/\D/g, "").length >= 7);
      return { idx: idx + 1, name, phoneNumber, valid };
    });
}

const QuickImportModal = ({ onClose, onSuccess, groupId }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const rows = useMemo(() => parseRows(text), [text]);
  const validRows = rows.filter((r) => r.valid);
  const invalidRows = rows.filter((r) => !r.valid);

  const canSubmit = validRows.length >= MIN && rows.length <= MAX && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setLoading(true);
      const payload = validRows.map(({ name, phoneNumber }) => ({ name, phoneNumber }));
      const data = groupId
        ? await quickImportToGroup(groupId, payload)
        : await quickImportCustomers(payload);
      setResult(data);
      const count = groupId ? data.added : data.imported;
      if (count > 0) {
        toast.success(`${groupId ? "Added" : "Imported"} ${count} customer${count !== 1 ? "s" : ""}`);
        onSuccess?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setText("");
    setResult(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ClipboardPaste className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Quick Import</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

          {!result ? (
            <>
              {/* Instructions */}
              <p className="text-sm text-gray-500">
                Paste customers — one per line. Name and phone number can be separated by a comma, tab, space, or nothing at all.
              </p>
              <div className="bg-gray-50 rounded-lg px-4 py-2 text-xs text-gray-500 font-mono space-y-0.5">
                <div>Arjun Menon 919567823410</div>
                <div>Sara Al Farsi, 971543290876</div>
                <div>Ravi Kumar	916382910457</div>
              </div>

              {/* Textarea */}
              <textarea
                className="w-full h-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono"
                placeholder={"Arjun Menon 919567823410\nSara Al Farsi 971543290876\nRavi Kumar 916382910457"}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />

              {/* Counter */}
              {rows.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <span className={`font-medium ${rows.length > MAX ? "text-red-600" : "text-gray-700"}`}>
                    {rows.length} row{rows.length !== 1 ? "s" : ""} detected
                  </span>
                  <span className="text-gray-400">·</span>
                  <span className="text-green-600 font-medium">{validRows.length} valid</span>
                  {invalidRows.length > 0 && (
                    <>
                      <span className="text-gray-400">·</span>
                      <span className="text-red-500 font-medium">{invalidRows.length} invalid (will be skipped)</span>
                    </>
                  )}
                  {rows.length > MAX && (
                    <>
                      <span className="text-gray-400">·</span>
                      <span className="text-red-600 font-medium">Maximum {MAX} rows</span>
                    </>
                  )}
                  {validRows.length > 0 && validRows.length < MIN && (
                    <>
                      <span className="text-gray-400">·</span>
                      <span className="text-orange-500 font-medium">Minimum {MIN} valid rows needed</span>
                    </>
                  )}
                </div>
              )}

              {/* Preview table */}
              {rows.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 w-8">#</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Name</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Phone</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((row) => (
                        <tr key={row.idx} className={row.valid ? "" : "bg-red-50"}>
                          <td className="px-3 py-2 text-gray-400 text-xs">{row.idx}</td>
                          <td className="px-3 py-2 text-gray-900">{row.name || <span className="text-gray-400 italic">—</span>}</td>
                          <td className="px-3 py-2 text-gray-700 font-mono text-xs">{row.phoneNumber || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2">
                            {row.valid
                              ? <CheckCircle className="w-4 h-4 text-green-500" />
                              : <AlertCircle className="w-4 h-4 text-red-400" />
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            /* Result view */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{groupId ? result.added : result.imported}</p>
                  <p className="text-sm text-green-700 mt-1">{groupId ? "Added to Group" : "Imported"}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{groupId ? result.created : result.skipped}</p>
                  <p className="text-sm text-blue-700 mt-1">{groupId ? "New Customers Created" : "Skipped (duplicates)"}</p>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-medium text-red-700 mb-1">Details:</p>
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{typeof e === "string" ? e : e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          {result ? (
            <>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Import More
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Importing..." : `Import ${validRows.length} Customer${validRows.length !== 1 ? "s" : ""}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickImportModal;
